import { Link } from "../api/model/link";
import { AddToCartRequest } from "../api/requests/add-to-cart-request";
import { UserCarts } from "../api/responses/get-user-carts-response";
import { Cart, CartEntry } from "api/model/mini-cart";
import DeleteEntryRequest from "@apis/requests/delete-entry-request";
import { UpdatedEntry } from "../api/responses/add-to-cart-response";
import config from "../environment/environment-config";
import cartStorage from "@globals/helpers/cart-storage.service";
import { JwtService, RequestHeaderService } from "globals/helpers";
import { IWindow } from "../api/model/window";
declare let window: IWindow;

export const CUSTOMER_BASE_URL = config.apiUrls.customerApi;
export const CONTEXT_PATH = config.contextPath;
export const JWT_TOKEN = config.token._jwt;
export const LOGIN_PATH = config.loginUrl;

export const API_KEY = config.cartConfig.apiKey;
export const BASE_CART_API_URL = config.cartConfig.baseCartApiURL;
export const USER_ID = config.cartConfig.userId;
export const BASE_SITE_ID = config.cartConfig.baseSiteId;

export const ADD_TO_CART_BEGIN = "ADD_TO_CART_BEGIN";
export const ADD_TO_CART_SUCCESS = "ADD_TO_CART_SUCCESS";
export const ADD_TO_CART_FAILURE = "ADD_TO_CART_FAILURE";
export const GET_CARTS_BEGIN = "GET_CARTS_BEGIN";
export const GET_CARTS_SUCCESS = "GET_CARTS_SUCCESS";
export const GET_CARTS_FAILURE = "GET_CARTS_FAILURE";
export const CREATE_CART_BEGIN = "CREATE_CART_BEGIN";
export const CREATE_CART_SUCCESS = "CREATE_CART_SUCCESS";
export const CREATE_CART_FAILURE = "CREATE_CART_FAILURE";
export const DELETE_CART_ENTRY_BEGIN = "DELETE_CART_ENTRY_BEGIN";
export const DELETE_CART_ENTRY_SUCCESS = "DELETE_CART_ENTRY_SUCCESS";
export const DELETE_CART_ENTRY_FAILURE = "DELETE_CART_ENTRY_FAILURE";

export const addToCartBegin = (request: AddToCartRequest) => ({
  type: ADD_TO_CART_BEGIN,
  payload: { request },
});

export const addToCartSuccess = (
  cart: Cart,
  updatedEntries: UpdatedEntry[]
) => ({
  type: ADD_TO_CART_SUCCESS,
  payload: { cart, updatedEntries },
});

export const addToCartFailure = (USER_ID, url, request) => ({
  type: ADD_TO_CART_FAILURE,
  payload: { errorDetails: { USER_ID, url, request } },
});

export const getCartsBegin = (url: string) => ({
  type: GET_CARTS_BEGIN,
  payload: { url },
});

export const getCartsSuccess = (cartsResponse: UserCarts) => ({
  type: GET_CARTS_SUCCESS,
  payload: { cartsResponse },
});

export const getCartsFailure = (url: string) => ({
  type: GET_CARTS_FAILURE,
  payload: { errorDetails: url },
});

export const createCartBegin = (url: string) => ({
  type: CREATE_CART_BEGIN,
  payload: { url },
});

export const createCartSuccess = (cart: Cart) => ({
  type: CREATE_CART_SUCCESS,
  payload: { cart },
});

export const createCartFailure = (url: string) => ({
  type: CREATE_CART_FAILURE,
  payload: { errorDetails: url },
});

export const deleteEntryBegin = (entry: CartEntry, cart: Cart) => ({
  type: DELETE_CART_ENTRY_BEGIN,
  payload: { entry, cart },
});

export const deleteEntrySuccess = (entry: CartEntry, cart: Cart) => ({
  type: DELETE_CART_ENTRY_SUCCESS,
  payload: { entry, cart },
});

export const deleteEntryFailure = (url, entry: any) => ({
  type: DELETE_CART_ENTRY_FAILURE,
  payload: { errorDetails: { url, entry } },
});

/**
 * @jsdoc method
 * @name updateQuickLinks
 * @methodOf actions.customerActions
 * @description description
 * Updates quick links list of currently logged in user
 */
export function updateQuickLinks(linksDto: Array<Link>) {
  return fetch(`${CUSTOMER_BASE_URL}/quick-links`, {
    credentials: "same-origin",
    method: "PUT",
    body: JSON.stringify({ quickLinks: linksDto }),
    ...RequestHeaderService.getDefaultHeaders(),
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error();
      }
    })
    .catch(() => {
      window.location.reload();
    });
}

export function isAnonymous() {
  return USER_ID === "anonymous";
}

export function getCartForAnonymous(cartGuid): Promise<UserCarts> {
  const url = `${BASE_CART_API_URL}/${BASE_SITE_ID}/users/${USER_ID}/carts/${cartGuid}?fields=FULL`;
  return fetch(url, {
    method: "GET",
    ...RequestHeaderService.getHeadersWithJwt(),
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error();
    }
    const updatedCart: Cart = await response.json();
    return Promise.resolve({ carts: [updatedCart] });
  });
}

export async function getCarts(): Promise<UserCarts> {
  if (!JwtService.isJwtValid() && !isAnonymous()) {
    await JwtService.updateJwt();
  }
  if (isAnonymous() && cartStorage.isEmpty()) {
    return Promise.resolve({ carts: [] });
  }
  if (isAnonymous() && !cartStorage.isEmpty()) {
    return Promise.resolve(cartStorage.getCarts());
  }
  const url = `${BASE_CART_API_URL}/${BASE_SITE_ID}/users/${USER_ID}/carts?fields=FULL`;
  return fetch(url, {
    method: "GET",
    ...RequestHeaderService.getHeadersWithJwt(),
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error();
    }
    const data = await response.json();
    return data;
  });
}

export function updateCarts(response) {
  if (USER_ID === "anonymous") {
    cartStorage.saveAll(response);
  }
  return (dispatch) => {
    dispatch(getCartsSuccess(response));
  };
}

/**
 * @jsdoc method
 * @name addToCart
 * @methodOf actions.customerActions
 * @description
 * Adds product to current user cart
 */

export function addToCart(request: AddToCartRequest) {
  const cartCode =
    USER_ID === "anonymous" ? request.cart.guid : request.cart.code;
  const url = `${BASE_CART_API_URL}/${BASE_SITE_ID}/users/${USER_ID}/carts/${cartCode}/entries?fields=FULL`;
  return async (dispatch) => {
    dispatch(addToCartBegin(request));
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(request.order),
        ...RequestHeaderService.getHeadersWithJwt(),
      });
      if (!response.ok) {
        throw new Error();
      }

      const updatedEntries: UpdatedEntry[] = await response.json();
      dispatch(addToCartSuccess(request.cart, updatedEntries));
      return updatedEntries;
    } catch (error) {
      dispatch(addToCartFailure(USER_ID, url, request));
    }
  };
}

export function createCart() {
  const url = `${BASE_CART_API_URL}/${BASE_SITE_ID}/users/${USER_ID}/carts`;
  return async (dispatch) => {
    dispatch(createCartBegin(url));
    try {
      const response = await fetch(url, {
        method: "POST",
        ...RequestHeaderService.getHeadersWithJwt(),
      });
      if (!response.ok) {
        throw new Error();
      }
      const cart: Cart = await response.json();
      dispatch(createCartSuccess(cart));
      return cart;
    } catch (error) {
      dispatch(createCartFailure(url));
    }
  };
}

export function deleteEntry(deleteEntryRequest: DeleteEntryRequest) {
  const url = `${BASE_CART_API_URL}/${BASE_SITE_ID}/users/${USER_ID}/carts/${deleteEntryRequest.cart.code}/entries/${deleteEntryRequest.entry.entryNumber}`;
  return (dispatch) => {
    dispatch(
      deleteEntryBegin(deleteEntryRequest.entry, deleteEntryRequest.cart)
    );
    return fetch(url, {
      method: "DELETE",
      ...RequestHeaderService.getHeadersWithJwt(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error();
        }
        dispatch(
          deleteEntrySuccess(deleteEntryRequest.entry, deleteEntryRequest.cart)
        );
      })
      .catch(() => {
        dispatch(deleteEntryFailure(url, deleteEntryRequest.entry));
      });
  };
}

import {
  SalesRepSearchRequest,
  SalesRepChooseCustomerRequest,
} from "../api/requests/sales-rep-search-request";

export const SALES_REP_BASE_URL = config.apiUrls.salesRepApi;

/**
 * @jsdoc method
 * @name getSalesRepSearchResults
 * @methodOf actions.customerActions
 * @description
 * Retrieves the list of available customer for salesRep
 */
export function getSalesRepSearchResults(request: SalesRepSearchRequest) {
  const url = SALES_REP_BASE_URL + "/search" + request.toQueryString();

  return fetch(url, { credentials: "same-origin", method: "GET" })
    .then((res) => {
      if (!res.ok) {
        throw new Error();
      } else {
        return res.json();
      }
    })
    .catch((error: Error) => {
      console.log(error);
    });
}

/**
 * @jsdoc method
 * @name postSalesRepSearchChooseCustomer
 * @methodOf actions.customerActions
 * @description
 * Sets the chosen customer for salesRep
 */
export function postSalesRepSearchChooseCustomer(
  request: SalesRepChooseCustomerRequest
) {
  const url = SALES_REP_BASE_URL + "/chooseCustomer";
  return fetch(url, {
    credentials: "same-origin",
    method: "POST",
    body: JSON.stringify(request),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json();
    })
    .catch((error: Error) => {
      console.log(error);
    });
}
